/*
 * Copyright (c) 2023. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import NotificationsIcon from '@mui/icons-material/Notifications';
import {
  Badge,
  Box,
  ClickAwayListener,
  Fade,
  IconButton,
  Paper,
  Popper,
  type Theme,
} from '@mui/material';
import {
  type AbsoluteURL,
  type ObjectIdMap,
  SalesforceObject,
  type UINotification,
} from '@poweralpha/common';
import { type InfiniteData, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import type { HTTPError } from 'ky';
import { sum } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getNotifications, markAllNotificationAsViewed, markNotificationAsViewed } from '../api';
import { getbnCRMObjectIdMap } from '../api/bncrm';
import { useAuth } from '../auth';
import usePANavigate from '../react-components/navigate/usePANavigate';
import NotificationPopperContent from './components/NotificationPopperContent';
import type { UINotificationsQueryResult } from './Notifications';
import useNotificationToasts from './useNotificationToasts';
import { formatNotifications } from './utils';

export interface NotificationsCenterStateParams {
  refreshNotifCenter?: boolean;
}

const PAGE_SIZE = 20; // # of notifications per page
const REFETCH_INTERVAL = 20; // secs

const paperSx = (theme: Theme) => ({
  bgcolor: theme.palette.background.default,
  overflowY: 'hidden',
  zIndex: 5,
  borderRadius: 2,
});

function NotificationCenter() {
  const { navigate, updateState, location } = usePANavigate<NotificationsCenterStateParams>();
  const { user } = useAuth();
  const anchorEl = useRef<HTMLButtonElement | null>(null);
  const notifToClearRef = useRef<string | null>(null);
  const notifToClearId = new URLSearchParams(location.search).get('clear');

  const [isOpen, setIsOpen] = useState(false);

  const projectIdMapQuery = useQuery<ObjectIdMap>({
    queryKey: ['project-id-map'],
    queryFn: () => getbnCRMObjectIdMap(SalesforceObject.project),
  });
  const projectIdMap = projectIdMapQuery.data;

  const { data, refetch, fetchNextPage, isFetching, isLoading } = useInfiniteQuery<
    UINotificationsQueryResult,
    HTTPError,
    InfiniteData<UINotificationsQueryResult>,
    ['notifications'],
    number
  >({
    queryKey: ['notifications'],
    queryFn: ({ pageParam = PAGE_SIZE }) =>
      getNotifications(PAGE_SIZE, pageParam - PAGE_SIZE).then((res) =>
        formatNotifications(res, projectIdMap, user?.email),
      ),
    initialPageParam: PAGE_SIZE,
    getNextPageParam: (_, p) => sum(p.map(({ rows }) => rows.length)) + PAGE_SIZE,
    enabled: Boolean(user) && Boolean(projectIdMap),
    refetchInterval: Boolean(user) && Boolean(projectIdMap) ? REFETCH_INTERVAL * 1e3 : false,
  });

  const markAsViewedMutation = useMutation<void, HTTPError, string>({
    mutationFn: (id) => markNotificationAsViewed(id),
    onSuccess: () => refetch(),
  });
  const markAsViewed = markAsViewedMutation.mutate;

  const markAllAsViewedMutation = useMutation<{ count: number }>({
    mutationFn: markAllNotificationAsViewed,
    onSuccess: () => refetch(),
  });
  const markAllAsViewed = markAllAsViewedMutation.mutate;

  const handleClick = useCallback(() => setIsOpen((prev) => !prev), []);

  const handleClickAway = useCallback(() => {
    if (!isOpen) return;
    setIsOpen(false);
  }, [isOpen]);

  const handleNotifClick = useCallback(
    ({ link, id }: UINotification, redirect = true) => {
      markAsViewed(id);
      handleClickAway();
      if (redirect) {
        navigate(link as AbsoluteURL);
      }
    },
    [handleClickAway, markAsViewed, navigate],
  );

  const handleMarkAllRead = useCallback(async () => markAllAsViewed(), [markAllAsViewed]);

  useNotificationToasts(data?.pages.at(0)?.unread, handleNotifClick);

  useEffect(() => {
    if (location.state?.refreshNotifCenter) {
      refetch();
      updateState({ refreshNotifCenter: false }, true);
    }
  }, [location.state?.refreshNotifCenter, refetch, updateState]);

  useEffect(() => {
    if (notifToClearId === notifToClearRef.current) return;
    if (notifToClearId) {
      markAsViewed(notifToClearId);
    }
    notifToClearRef.current = notifToClearId;
  }, [markAsViewed, notifToClearId]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box>
        <Badge
          badgeContent={data?.pages?.[0].unread.length}
          color="error"
          overlap="circular"
          max={99}
        >
          <IconButton onClick={handleClick} ref={anchorEl} color="secondary" size="small">
            <NotificationsIcon />
          </IconButton>
        </Badge>
        <Popper
          open={isOpen}
          placement="bottom-end"
          anchorEl={anchorEl.current}
          transition
          sx={{ zIndex: 1500 }}
          onClick={(e) => e.stopPropagation()}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={300}>
              <Paper elevation={3} sx={paperSx}>
                <NotificationPopperContent
                  data={data}
                  fetchNextPage={fetchNextPage}
                  pageSize={PAGE_SIZE}
                  isLoading={isLoading}
                  isFetching={isFetching}
                  handleNotifClick={handleNotifClick}
                  handleClickAway={handleClickAway}
                  handleMarkAllRead={handleMarkAllRead}
                />
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

export default NotificationCenter;
